import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Camera, Sparkles, LineChart, Flame, Star, Check, ArrowRight, Zap, Apple } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { HERO_STATS, FEATURES, HOW_STEPS, TESTIMONIALS, FAQS, PRICING } from '../mock';
import { detectCurrency, formatPrice } from '../lib/currency';
import { useAuth } from '../context/AuthContext';
import { useState, useMemo } from 'react';
import Seo from '../components/Seo';
import AuthModal from '../components/AuthModal';

const iconMap = { Camera, Sparkles, LineChart, Flame };

const Landing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const currency = useMemo(() => detectCurrency(), []);
  const startTrial = () => { if (user) navigate('/app'); else setAuthOpen(true); };

  return (
    <div className="min-h-screen bg-[#fefdf9]">
      <Seo
        title="CalorieSnap — Snap a meal, know your calories and macros"
        description="The smart photo calorie counter. Take a picture of any meal and CalorieSnap's AI returns exact calories, protein, carbs and fat in seconds. Free forever, Pro from $5.99/week."
        canonical="https://www.thecaloriesnap.com/"
      />
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-200/40 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-orange-200/30 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fadeup">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold mb-6">
              <Zap className="w-3.5 h-3.5" /> 1-day free trial · 3 scans · no credit card
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-emerald-950 leading-[1.05] mb-6">
              Snap it.<br/>
              <span className="text-emerald-700">Know</span> it.<br/>
              <span className="italic font-medium">Own</span> your nutrition.
            </h1>
            <p className="text-lg text-emerald-950/70 mb-8 max-w-lg leading-relaxed">The smart and easy way to analyse your calorie intake. Take a photo of any meal — our AI reads the plate and returns precise calories & macros in seconds.</p>
            <div className="flex flex-wrap gap-3">
              <Button size="lg" className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full h-14 px-8 text-base font-semibold pulse-ring" onClick={startTrial}>
                Start your free trial <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full h-14 px-8 text-base border-emerald-950/15 hover:bg-emerald-50" asChild>
                <a href="#how">See how it works</a>
              </Button>
            </div>
            <div className="grid grid-cols-4 gap-4 mt-12 max-w-md">
              {HERO_STATS.map(s => (
                <div key={s.label}>
                  <div className="font-display text-2xl font-bold text-emerald-950">{s.value}</div>
                  <div className="text-[11px] uppercase tracking-wider text-emerald-950/50 mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative animate-fadeup" style={{ animationDelay: '0.15s' }}>
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl border-8 border-white rotate-2 hover:rotate-0 transition-transform duration-500">
              <img src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?crop=entropy&cs=srgb&fm=jpg&w=900&q=85" alt="Healthy bowl" className="w-full h-[520px] object-cover" />
              <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur rounded-2xl p-4 shadow-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-display font-bold text-emerald-950">Fresh Buddha bowl</span>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">96% match</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  {[['540','kcal'],['22g','protein'],['68g','carbs'],['18g','fat']].map(([v,l]) => (
                    <div key={l} className="bg-emerald-50 rounded-lg py-1.5">
                      <div className="font-bold text-emerald-900 text-sm">{v}</div>
                      <div className="text-[10px] uppercase text-emerald-800/60">{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="absolute -top-6 -left-6 bg-orange-400 text-white rounded-2xl px-4 py-3 shadow-lg -rotate-6 hidden lg:block">
              <div className="text-xs opacity-90">Streak</div>
              <div className="font-display font-bold text-2xl flex items-center gap-1"><Flame className="w-5 h-5" /> 14 days</div>
            </div>
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl px-5 py-3 shadow-xl rotate-3 hidden lg:block">
              <div className="flex items-center gap-1 text-amber-500 mb-1">{[...Array(5)].map((_,i)=><Star key={i} className="w-4 h-4 fill-current" />)}</div>
              <div className="text-xs text-emerald-950/70">Rated 4.9 by 12k+ users</div>
            </div>
          </div>
        </div>
      </section>

      {/* TWO CO-EQUAL PRIMARY FEATURES */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-emerald-700 font-semibold text-sm uppercase tracking-widest mb-3">Two ways to use CalorieSnap</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-emerald-950">Snap a plate. Or snap your fridge.</h2>
            <p className="text-emerald-950/70 text-lg mt-3">Both start with a single photo. Both give you back real numbers in seconds.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* Plate feature */}
            <div className="group rounded-[2rem] overflow-hidden border border-emerald-950/5 bg-emerald-50/40 hover:shadow-2xl transition-shadow flex flex-col">
              <div className="relative h-64 overflow-hidden">
                <img src="https://images.unsplash.com/photo-1512621776951-a57141f2eefd?crop=entropy&cs=srgb&fm=jpg&w=900&q=80" alt="Plated meal photo" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-emerald-800 text-xs font-bold uppercase tracking-widest"><Camera className="w-3.5 h-3.5" />Snap your plate</div>
              </div>
              <div className="p-8 flex-1 flex flex-col">
                <h3 className="font-display text-2xl md:text-3xl font-bold text-emerald-950 mb-3">Know your calories in seconds.</h3>
                <p className="text-emerald-950/70 mb-5 leading-relaxed">Take a photo of any meal — home-cooked or restaurant — and AI returns the exact calories, protein, carbs, and fat. Fine-tune ingredients, save to your diary, watch your rings fill.</p>
                <ul className="space-y-2 mb-6">
                  {['Works with any dish or cuisine','Instant macro breakdown','Adjust portions with a tap'].map(x => (
                    <li key={x} className="flex items-start gap-2.5 text-emerald-950/90 text-sm"><Check className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" strokeWidth={3} />{x}</li>
                  ))}
                </ul>
                <Button size="lg" className="mt-auto bg-emerald-700 hover:bg-emerald-800 text-white rounded-full h-12" onClick={startTrial}><Camera className="w-4 h-4 mr-2" />Snap a plate</Button>
              </div>
            </div>

            {/* Fridge / recipe suggestions feature */}
            <div className="group rounded-[2rem] overflow-hidden border border-emerald-950/5 bg-orange-50/40 hover:shadow-2xl transition-shadow flex flex-col">
              <div className="relative h-64 overflow-hidden">
                <img src="https://images.pexels.com/photos/1435904/pexels-photo-1435904.jpeg?auto=compress&cs=tinysrgb&w=900" alt="Fridge with ingredients" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-orange-700 text-xs font-bold uppercase tracking-widest"><Sparkles className="w-3.5 h-3.5" />Snap your fridge</div>
              </div>
              <div className="p-8 flex-1 flex flex-col">
                <h3 className="font-display text-2xl md:text-3xl font-bold text-emerald-950 mb-3">Get 5 dinner ideas from what you have.</h3>
                <p className="text-emerald-950/70 mb-5 leading-relaxed">Photograph your fridge or pantry and AI spots the ingredients, then suggests 5 recipes that fit your daily calorie target. Swipe right on the ones you'd cook — the app quietly learns your taste.</p>
                <ul className="space-y-2 mb-6">
                  {['AI detects ingredients from one photo','5 tailored recipes with real macros','Tinder-style swipes teach your preferences'].map(x => (
                    <li key={x} className="flex items-start gap-2.5 text-emerald-950/90 text-sm"><Check className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" strokeWidth={3} />{x}</li>
                  ))}
                </ul>
                <Button size="lg" className="mt-auto bg-orange-500 hover:bg-orange-600 text-white rounded-full h-12" onClick={startTrial}><Sparkles className="w-4 h-4 mr-2" />Snap your fridge</Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LOGO STRIP */}
      <section className="border-y border-black/5 bg-white py-8 overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <p className="text-center text-xs uppercase tracking-widest text-emerald-950/50 mb-6">Featured in</p>
          <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-4 opacity-60">
            {['TechCrunch','Wired','Forbes','ProductHunt','Lifehacker','Runner’s World'].map(l => (
              <span key={l} className="font-display text-xl font-semibold text-emerald-950/80">{l}</span>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-emerald-700 font-semibold text-sm uppercase tracking-widest mb-3">How it works</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-emerald-950 mb-4">Three taps, whichever path you pick</h2>
            <p className="text-emerald-950/70 text-lg">Whether you're logging tonight's plate or planning dinner from your fridge, the flow is the same.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {HOW_STEPS.map((s, i) => (
              <div key={s.step} className="relative bg-white rounded-3xl p-8 border border-emerald-950/5 shadow-sm hover:shadow-lg transition-shadow">
                <div className="font-display text-5xl font-bold text-emerald-100 mb-2">{s.step}</div>
                <h3 className="font-display text-2xl font-bold text-emerald-950 mb-2">{s.title}</h3>
                <p className="text-emerald-950/70">{s.desc}</p>
                {i < 2 && <ArrowRight className="hidden md:block absolute -right-5 top-1/2 -translate-y-1/2 w-8 h-8 text-emerald-200" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-24 bg-[#fbf9f2]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-emerald-700 font-semibold text-sm uppercase tracking-widest mb-3">Why CalorieSnap</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-emerald-950 mb-4">Everything you need. Nothing you don’t.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {FEATURES.map((f) => {
              const Icon = iconMap[f.icon];
              return (
                <div key={f.title} className="bg-white rounded-3xl p-8 border border-emerald-950/5 flex gap-5 hover:-translate-y-1 transition-transform duration-300">
                  <div className="shrink-0 w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center"><Icon className="w-6 h-6" /></div>
                  <div>
                    <h3 className="font-display text-xl font-bold text-emerald-950 mb-1.5">{f.title}</h3>
                    <p className="text-emerald-950/70 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SHOWCASE */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="grid grid-cols-2 gap-4">
            {['https://images.unsplash.com/photo-1546069901-ba9599a7e63c?crop=entropy&cs=srgb&fm=jpg&w=500&q=80','https://images.unsplash.com/photo-1498837167922-ddd27525d352?crop=entropy&cs=srgb&fm=jpg&w=500&q=80','https://images.unsplash.com/photo-1556040220-4096d522378d?crop=entropy&cs=srgb&fm=jpg&w=500&q=80','https://images.pexels.com/photos/3248288/pexels-photo-3248288.jpeg?auto=compress&cs=tinysrgb&w=500'].map((src, i) => (
              <div key={i} className={`rounded-3xl overflow-hidden ${i%2 ? 'mt-8' : ''}`}><img src={src} className="w-full h-64 object-cover hover:scale-105 transition-transform duration-500" alt="food" /></div>
            ))}
          </div>
          <div>
            <p className="text-emerald-700 font-semibold text-sm uppercase tracking-widest mb-3">Built for real life</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-emerald-950 mb-6">From street food to Sunday roast.</h2>
            <p className="text-emerald-950/70 text-lg mb-8">CalorieSnap recognises hundreds of thousands of dishes from around the world — including mixed plates, restaurant meals, and grandma’s secret recipes.</p>
            <ul className="space-y-3">
              {['Works offline in a pinch','Fine-tune ingredients in one tap','Export data anytime','Connects with Apple Health & Google Fit (coming soon)'].map(x => (
                <li key={x} className="flex items-start gap-3 text-emerald-950"><span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center shrink-0 mt-0.5"><Check className="w-3.5 h-3.5" strokeWidth={3} /></span>{x}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-emerald-950 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-orange-300 font-semibold text-sm uppercase tracking-widest mb-3">Loved by 85k+</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">People are eating better with CalorieSnap.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map(t => (
              <div key={t.name} className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-8">
                <div className="flex gap-1 text-amber-300 mb-4">{[...Array(5)].map((_,i)=><Star key={i} className="w-4 h-4 fill-current" />)}</div>
                <p className="text-lg leading-relaxed mb-6">“{t.quote}”</p>
                <div className="flex items-center gap-3"><img src={t.avatar} className="w-11 h-11 rounded-full" alt={t.name} /><div><div className="font-semibold">{t.name}</div><div className="text-white/60 text-sm">{t.role}</div></div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING TEASER */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-emerald-700 font-semibold text-sm uppercase tracking-widest mb-3">Simple pricing</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-emerald-950 mb-4">Free forever. Go Pro when you’re hooked.</h2>
            <p className="text-emerald-950/70 text-lg">Prices shown in {currency}. Stripe charges in your local currency at checkout.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
            {PRICING.map(p => (
              <div key={p.id} className={`relative rounded-3xl p-6 border flex flex-col ${p.highlighted ? 'bg-emerald-950 text-white border-emerald-950' : 'bg-white text-emerald-950 border-emerald-950/10'}`}>
                {p.badge && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-400 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">{p.badge}</span>}
                <div className="mb-3"><h3 className="font-display text-xl font-bold">{p.name}</h3><p className={`text-sm mt-0.5 ${p.highlighted ? 'text-white/70' : 'text-emerald-950/60'}`}>{p.tagline}</p></div>
                <div className="flex items-baseline gap-1 mb-5"><span className="font-display text-3xl font-bold">{p.usd === 0 ? 'Free' : formatPrice(p.usd, currency)}</span>{p.usd !== 0 && <span className={`text-sm ${p.highlighted ? 'text-white/70' : 'text-emerald-950/60'}`}>{p.period}</span>}</div>
                <ul className="space-y-2 mb-6 flex-1">
                  {p.features.slice(0,4).map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm"><Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${p.highlighted ? 'text-orange-300' : 'text-emerald-700'}`} strokeWidth={3} /><span className={p.highlighted ? 'text-white/90' : 'text-emerald-950/80'}>{f}</span></li>
                  ))}
                </ul>
                <Button className={`w-full rounded-full h-10 text-sm ${p.highlighted ? 'bg-orange-400 hover:bg-orange-500 text-white' : p.usd === 0 ? 'bg-white text-emerald-800 border border-emerald-200 hover:bg-emerald-50' : 'bg-emerald-700 hover:bg-emerald-800 text-white'}`} onClick={startTrial}>{p.cta}</Button>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-emerald-950/50 mt-6">See full details on the <Link to="/pricing" className="underline">pricing page</Link>.</p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 bg-[#fbf9f2]">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-emerald-700 font-semibold text-sm uppercase tracking-widest mb-3">Questions</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-emerald-950">Frequently asked</h2>
          </div>
          <Accordion type="single" collapsible className="w-full space-y-2">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`i${i}`} className="bg-white rounded-2xl border border-emerald-950/5 px-5">
                <AccordionTrigger className="font-display font-semibold text-lg text-emerald-950 text-left hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-emerald-950/70 leading-relaxed pb-4">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="relative rounded-[2.5rem] bg-gradient-to-br from-emerald-700 to-emerald-800 p-12 md:p-16 text-center text-white overflow-hidden">
            <div className="absolute -top-10 -right-10 w-56 h-56 bg-orange-400/30 rounded-full blur-3xl" />
            <div className="absolute -bottom-10 -left-10 w-56 h-56 bg-white/10 rounded-full blur-3xl" />
            <Apple className="w-10 h-10 mx-auto mb-4 opacity-70" />
            <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">Your healthier week starts today.</h2>
            <p className="text-white/80 text-lg mb-8 max-w-lg mx-auto">Join 85,000+ people who stopped guessing and started knowing. 1-day free trial with 3 scans, no card required.</p>
            <Button size="lg" className="bg-white text-emerald-800 hover:bg-emerald-50 rounded-full h-14 px-10 text-base font-semibold" onClick={startTrial}>Start free trial <ArrowRight className="ml-2 w-4 h-4" /></Button>
          </div>
        </div>
      </section>

      <Footer />
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />
    </div>
  );
};

export default Landing;
